<template>
  <div class='results-table'>
    <v-col cols='7'>
      <v-text-field v-model='search' append-icon='fas fa-search' label='Search' single-line hide-details></v-text-field>
    </v-col>
    <v-data-table
      :items-per-page='20'
      :footer-props='{itemsPerPageOptions: [5,10,20,64,100,-1]}'
      :headers='resultsHeaders'
      :items='resultsItems'
      must-sort
      :search='search'
      :options="{sortBy: ['num']}"
    >
      <template v-slot:item='{item}'>
        <tr :key='item.num' style='text-align: left' :class="{'winner': item.num <= eventSize, 'first': item.num === 1, 'second': item.num === 2, 'third': item.num === 3}">
          <td v-if='!psychSheet'>{{item.num}}</td>
          <td>
            <a v-if='item.wcaID !== null' :href='`https://www.worldcubeassociation.org/persons/${item.wcaID}`'>{{item.solverName}}</a>
            <span v-else>{{item.solverName}}</span>
          </td>
          <td v-if='!psychSheet'>
            <v-chip v-for='(time, i) in item.times' :key='i'>{{displayTimeWithMetadata(time)}}</v-chip>
          </td>
          <td v-if='!psychSheet'>{{displayTime(item.average)}}</td>
          <td v-if='!psychSheet'>{{displayTime(item.single)}}</td>
          <td v-if='!psychSheet'>
            <router-link v-if='showVideoEvidence' style='text-decoration: none;' :to='`/tpc/results/${roundID}/${item.profileID}o${item.solverOrder}`'><v-icon color='primary'>fas fa-external-link-alt</v-icon></router-link>
          </td>
          <td v-if='!psychSheet'>{{item.times && item.times[0] && item.times[0].openTime && formatUTCTime(item.times[0].openTime)}}</td>
          <td v-if='!psychSheet'>{{item.times && item.times[0] && item.times[0].submitTime && formatUTCTime(item.times[0].submitTime)}}</td>
          <td v-if='!psychSheet'>
            <v-tooltip v-if='!item.videoLink && item.times && item.times.length > 0' top>
              <template v-slot:activator='{on}'>
                <v-icon color='#ff0000' v-on='on'>fas fa-times-circle</v-icon>
              </template>
              <span>Video link must be submitted to be considered for bracket stage.</span>
            </v-tooltip>
            <v-tooltip v-else-if='item.videoLink && !showVideoEvidence' top>
              <template v-slot:activator='{on}'>
                <v-icon color='#717171' v-on='on'>fa fa-user-secret</v-icon>
              </template>
              <span>Video evidence is hidden until you have submitted results for this event.</span>
            </v-tooltip>
            <a v-else :href='item.videoLink'>{{item.videoLink}}</a>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-btn class='refresh-btn' depressed color='deep-purple' dark @click="$emit('refresh')">REFRESH<v-icon style='padding-left: 10px;'>fas fa-redo</v-icon></v-btn>
  </div>
</template>

<script>
import RoundsService from '@/services/TPC/RoundsService'

import utils from '@/modules/utils'
import {DateTime} from 'luxon'

const timeComparator = (a, b) => {
  if (a.metadata !== 'ok' && a.metadata !== '+2') return 1
  if (b.metadata !== 'ok' && b.metadata !== '+2') return -1
  return a.time - b.time
}
const timeSummer = (acc, v) => (acc + ((v.metadata !== 'ok' && v.metadata !== '+2') ? Infinity : v.time))
// calculates average given a sorted list
const averageCalc = (a) => {
  const numRemoved = a.length > 3 ? Math.ceil(a.length * 0.05) : 0
  return Math.round(a.slice(numRemoved, a.length - numRemoved).reduce(timeSummer, 0) / (a.length - 2 * numRemoved))
}

const numSorter = (a, b) => {
  return a - b
}

export default {
  name: 'results-table',
  data () {
    return {
      search: '',
      showVideoEvidence: false,
    }
  },
  props: {
    roundID: String,
    roundType: String,
    roundStatus: String,
    roundSolvers: Object,
    eventSize: Number,
  },
  methods: {
    displayTime (time) {
      return utils.displayTime(time)
    },
    displayTimeWithMetadata (time) {
      return utils.displayTimeWithMetadata(time)
    },
    formatUTCTime (iso) {
      let dt = DateTime.fromISO(iso)
      return dt.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
    }
  },
  computed: {
    psychSheet () {
      return this.$route.path.endsWith('/psychsheet')
    },
    resultsItems () {
      if (!this.roundSolvers || Object.keys(this.roundSolvers).length === 0) return []

      let items = []
      for (let profileID in this.roundSolvers) {
        let times = this.roundSolvers[profileID].times ? this.roundSolvers[profileID].times.slice() : []
        let average = NaN
        if (times && times.length) {
          times.sort(timeComparator)
          average = averageCalc(times)
        }

        items.push({
          ...this.roundSolvers[profileID],
          average,
          single: times.length > 0 ? ((times[0].metadata === 'ok' || times[0].metadata === '+2') ? times[0].time : Infinity) : NaN,
          profileID
        })
      }

      let sortBy = 'average'
      if (this.roundType === '3BLD') sortBy = 'single'
      items.sort((a, b) => {
        if (!!a.videoLink ^ !!b.videoLink) return a.videoLink ? -1 : 1 // if the video link status is different
        // if video link status is same (ie, both have submitted or both haven't submitted) then compare based on averages (or single if 3BLD)
        let newA = a[sortBy] || Infinity // setup newA to handle case where average = 0 (ie, times not submitted)
        let newB = b[sortBy] || Infinity
        return newA - newB
      })
      for (let i = 0; i < items.length; i++) {
        items[i].num = (items[i].times && items[i].times.length > 0 && items[i][sortBy] !== Infinity && items[i].videoLink) ? i + 1 : '-'
      }
      return items
    },
    resultsHeaders () {
      if (this.psychSheet) {
        return [{text: 'Name', value: 'solverName', sortable: true}]
      }
      return [
        {text: '#', width: '3%', value: 'num', sort: (a, b) => {
          if (a === '-') return 1
          if (b === '-') return -1
          return a - b
        }, sortable: true},
        {text: 'Name', value: 'solverName', width: '17%', sortable: true},
        {
          text: 'Times',
          value: 'times',
          width: '25%',
          sortable: true,
          sort: (ogA, ogB) => {
            if (!ogA || ogA.length === 0) return 1
            if (!ogB || ogB.length === 0) return -1
            let a = ogA.slice()
            let b = ogB.slice()
            a.sort(timeComparator)
            b.sort(timeComparator)
            return averageCalc(a) - averageCalc(b)
          }
        },
        {text: 'Average', width: '5%', value: 'average', sortable: true, sort: numSorter},
        {text: 'Single', width: '5%', value: 'single', sortable: true, sort: numSorter},
        {text: 'Scrambles', value: 'scrambles', sortable: false},
        {text: 'Open Time', value: 'openTime', sortable: true},
        {text: 'Submit Time', value: 'submitTime', sortable: true},
        {text: 'Video Link', value: 'videoLink', sortable: true}
      ]
    },
    // PROFILEID FOR WATCHING TO SEE IF WE SHOULD SHOW VIDEO EVIDENCE OR NOT
    profileID () {
      return this.$store.state.loginSettings && this.$store.state.loginSettings.profileID
    },
  },
  watch: {
    profileID: {
      immediate: true,
      async handler () {
        this.showVideoEvidence = this.showVideoEvidence || (this.$store.state.loginSettings && this.$store.state.loginSettings.adminAvailable)
        if (!this.showVideoEvidence && this.profileID) {
          // TODO: remove this client side validation
          const {data} = await RoundsService.listResults('solver', {
            roundID: this.roundID,
            profileID: this.profileID
          })
          if (data.success) {
            this.showVideoEvidence = true
          }
        }
      }
    },
  },
  async created () {
    this.showVideoEvidence = this.showVideoEvidence || this.roundStatus == 'REVIEW' || this.roundStatus == 'CLOSED'
  }
}
</script>

<style scoped>
.results-table {
  display: block;
  width: 100%;
  margin: auto;
}
@media (min-width: 1904px) {
  .results-table {
    max-width: 1785px !important;
  }
}

tr.winner {
  background-color: rgba(0, 255, 0, 0.4);
}
tr.first {
  background-color: rgba(255, 213, 0, 0.835);
}
tr.second {
  background-color: rgba(172, 172, 172, 0.767);
}
tr.third {
  background-color: rgba(255, 115, 0, 0.794);
}
.theme--light.v-chip:not(.v-chip--active) {
    background: rgba(240, 240, 240, 0.8);
}

.refresh-btn {
  position: absolute;
  right: 5vw;
  top: 2vh;
}
</style>
